import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { adminService } from '../../services/api';
import { Medicament } from '../../types';

interface MedicamentFormProps {
  medicament?: Medicament | null;
  onClose: () => void;
}

const MedicamentForm: React.FC<MedicamentFormProps> = ({ medicament, onClose }) => {
  const [formData, setFormData] = useState({
    nom: '',
    forme: '',
    dosage_standard: '',
    indication: '',
    contre_indication: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const queryClient = useQueryClient();

  useEffect(() => {
    if (medicament) {
      setFormData({
        nom: medicament.nom,
        forme: medicament.forme,
        dosage_standard: medicament.dosage_standard,
        indication: medicament.indication || '',
        contre_indication: medicament.contre_indication || ''
      });
    }
  }, [medicament]);

  const createMutation = useMutation({
    mutationFn: (data: any) => adminService.createMedicament(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-medicaments'] });
      onClose();
    },
    onError: (error: any) => {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => adminService.updateMedicament(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-medicaments'] });
      onClose();
    },
    onError: (error: any) => {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (medicament) {
      updateMutation.mutate({ id: medicament.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const formes = [
    'Comprimé',
    'Gélule',
    'Capsule',
    'Sirop',
    'Solution',
    'Suspension',
    'Injection',
    'Pommade',
    'Crème',
    'Gel',
    'Spray',
    'Gouttes',
    'Suppositoire',
    'Patch'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {medicament ? 'Modifier le médicament' : 'Nouveau médicament'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            title="Fermer le formulaire"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nom du médicament */}
            <div>
              <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
                Nom du médicament *
              </label>
              <input
                type="text"
                id="nom"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className={`w-full px-3 py-2 text-gray-900 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.nom ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom}</p>}
            </div>

            {/* Forme pharmaceutique */}
            <div>
              <label htmlFor="forme" className="block text-sm font-medium text-gray-700 mb-1">
                Forme pharmaceutique *
              </label>
              <select
                id="forme"
                name="forme"
                value={formData.forme}
                onChange={handleChange}
                className={`w-full px-3 py-2 text-gray-900 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.forme ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Sélectionner une forme</option>
                {formes.map(forme => (
                  <option key={forme} value={forme}>{forme}</option>
                ))}
              </select>
              {errors.forme && <p className="text-red-500 text-sm mt-1">{errors.forme}</p>}
            </div>

            {/* Dosage standard */}
            <div>
              <label htmlFor="dosage_standard" className="block text-sm font-medium text-gray-700 mb-1">
                Dosage standard *
              </label>
              <input
                type="text"
                id="dosage_standard"
                name="dosage_standard"
                value={formData.dosage_standard}
                onChange={handleChange}
                placeholder="Ex: 500mg, 100mg/ml"
                className={`w-full px-3 py-2 text-gray-900 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.dosage_standard ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.dosage_standard && <p className="text-red-500 text-sm mt-1">{errors.dosage_standard}</p>}
            </div>
          </div>

          {/* Indication */}
          <div>
            <label htmlFor="indication" className="block text-sm font-medium text-gray-700 mb-1">
              Indications thérapeutiques
            </label>
            <textarea
              id="indication"
              name="indication"
              value={formData.indication}
              onChange={handleChange}
              rows={3}
              placeholder="Ex: Douleur, fièvre, inflammation..."
              className={`w-full px-3 py-2 text-gray-900 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.indication ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.indication && <p className="text-red-500 text-sm mt-1">{errors.indication}</p>}
          </div>

          {/* Contre-indication */}
          <div>
            <label htmlFor="contre_indication" className="block text-sm font-medium text-gray-700 mb-1">
              Contre-indications
            </label>
            <textarea
              id="contre_indication"
              name="contre_indication"
              value={formData.contre_indication}
              onChange={handleChange}
              rows={3}
              placeholder="Ex: Allergie aux pénicillines, insuffisance rénale..."
              className={`w-full px-3 py-2 text-gray-900 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.contre_indication ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.contre_indication && <p className="text-red-500 text-sm mt-1">{errors.contre_indication}</p>}
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
            >
              {createMutation.isPending || updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicamentForm;